import { useOrderCheckpointsMetadata } from '@/api/hooks/useIntegrationLogs'
import type { SagaType } from '@/constants/sagaTypes'
import { Checkbox, Typography } from 'antd'
import { useMemo } from 'react'

const { Text } = Typography

export interface SilenceableSaga {
  saga: SagaType
  label: string
}

/**
 * The sagas a user can silence, in the server's checkpoint order. Deduped by
 * saga: several checkpoints can share one (`completed` and `status_updates`
 * are both progress_status) and silencing applies per saga, so one checkbox
 * per thing that can actually be silenced.
 */
export function useSilenceableSagas(): SilenceableSaga[] {
  const { data: metadata } = useOrderCheckpointsMetadata()

  return useMemo(() => {
    const seen = new Set<string>()
    const result: SilenceableSaga[] = []
    for (const entry of Object.values(metadata ?? {})) {
      if (seen.has(entry.saga)) continue
      seen.add(entry.saga)
      result.push({
        saga: entry.saga,
        label: entry.friendly_saga_name ?? entry.saga,
      })
    }
    return result
  }, [metadata])
}

interface SilencingCheckboxListProps {
  sagas: SilenceableSaga[]
  value: SagaType[]
  onChange: (next: SagaType[]) => void
  disabled?: boolean
}

/** Shared by the per-row menu and the bulk dialog, so the two can't drift. */
export function SilencingCheckboxList({
  sagas,
  value,
  onChange,
  disabled,
}: SilencingCheckboxListProps) {
  const selected = new Set(value)
  const allSelected =
    sagas.length > 0 && sagas.every((s) => selected.has(s.saga))

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
      <Checkbox
        checked={allSelected}
        indeterminate={!allSelected && selected.size > 0}
        disabled={disabled || sagas.length === 0}
        onChange={(e) =>
          onChange(e.target.checked ? sagas.map((s) => s.saga) : [])
        }
      >
        <Text strong style={{ fontSize: 13 }}>
          All checkpoints
        </Text>
      </Checkbox>
      <div style={{ borderTop: '1px solid var(--border)', margin: '2px 0' }} />
      {sagas.map(({ saga, label }) => (
        <Checkbox
          key={saga}
          checked={selected.has(saga)}
          disabled={disabled}
          onChange={(e) =>
            onChange(
              e.target.checked
                ? [...value, saga]
                : value.filter((s) => s !== saga),
            )
          }
        >
          <Text style={{ fontSize: 13 }}>{label}</Text>
        </Checkbox>
      ))}
    </div>
  )
}

/** The panel chrome both silencing popups sit in. */
export function SilencingPanel({
  children,
  title,
}: { children: React.ReactNode; title: string }) {
  return (
    <div
      style={{
        backgroundColor: 'var(--bg-1)',
        border: '1px solid var(--border)',
        borderRadius: 8,
        padding: '8px 12px',
        boxShadow: 'var(--shadow-2, 0 2px 8px rgba(0, 0, 0, 0.15))',
      }}
      // Keep clicks off the row -- the grid expands a row on any cell click.
      onClick={(e) => e.stopPropagation()}
    >
      <Text
        type='secondary'
        style={{ fontSize: 11, display: 'block', marginBottom: 6 }}
      >
        {title}
      </Text>
      {children}
    </div>
  )
}
